import type { DeepPartial } from '@tryiris-ai/common.protojs';
import { CalibrationActionType, type State } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { DetectionMode } from '@tryiris-ai/common.protojs/autotracking/v2/state';
import type { ReadonlyDeep } from 'type-fest';
import { isTrialCameraLocked } from './camera-access.js';

// Guard the submission boundary, including callers which kept a selection or
// queued a callback before expiry. Restore/configuration and stop deltas stay
// valid, and an already-enabled output is not interrupted by this policy.
type CurrentCameraState = ReadonlyDeep<State> | DeepPartial<State>;

export function assertTrialCameraMutationAllowed(
  roomId: string,
  agentId: string,
  set: DeepPartial<State>,
  current: CurrentCameraState
): void {
  const reject = (deviceId: string | undefined): void => {
    if (isTrialCameraLocked(roomId, agentId, deviceId))
      throw new Error('Camera locked after Standard trial expiry');
  };
  const commands = set.command?.device;
  if (commands !== undefined) {
    for (const command of [
      commands.move,
      commands.moveTo,
      commands.image?.refresh,
      commands.image?.applySuggestedSettings,
      commands.fastFrame,
      commands.enhancedMove,
      commands.nativePreset
    ]) {
      if (command !== undefined) reject(command.id);
    }
    if (commands.stream?.enable?.enable === true) reject(commands.stream.enable.id);
  }
  for (const [deviceId, device] of Object.entries(set.control?.device ?? {})) {
    if (device === undefined || !isTrialCameraLocked(roomId, agentId, deviceId)) continue;
    // Restoration carries the saved URX alongside its full configuration. It
    // must remain loadable so locked cameras retain settings and can be removed.
    if (device.urx !== undefined) continue;
    const movement = device.movement;
    const calibrationStop =
      movement?.calibration?.action === CalibrationActionType.CALIBRATION_ACTION_STOP;
    const movementAction =
      movement !== undefined &&
      (!calibrationStop || Object.keys(movement).some((key) => key !== 'calibration'));
    const settingAction = Object.values(device.deviceSettings ?? {}).some(
      (setting) => setting !== undefined
    );
    const before = current.control?.device?.[deviceId]?.egress;
    const output = device.egress;
    const startsNDI = output?.ndiOutput?.enable === true && before?.ndiOutput?.enable !== true;
    const startsRecording =
      output?.recording?.enable === true && before?.recording?.enable !== true;
    const startsRTMP = Object.entries(output?.rtmp?.destinations ?? {}).some(
      ([id, destination]) =>
        destination?.enable === true && before?.rtmp?.destinations?.[id]?.enable !== true
    );
    if (movementAction || settingAction || startsNDI || startsRecording || startsRTMP)
      reject(deviceId);
  }
  const hex = (id: ArrayLike<number> | undefined): string | undefined =>
    id === undefined
      ? undefined
      : Array.from(id, (byte) => byte.toString(16).padStart(2, '0')).join('');
  const isRestoring = (id: string | undefined): boolean =>
    id !== undefined && set.control?.device?.[id]?.urx !== undefined;
  for (const [key, reservation] of Object.entries(
    set.control?.studio?.autoTrackingReservation ?? {}
  )) {
    const id = hex(reservation?.cameraId);
    if (!isRestoring(id) && current.control?.studio?.autoTrackingReservation?.[key] === undefined)
      reject(id);
  }
  for (const [key, worker] of Object.entries(set.control?.studio?.autoTrackingWorker ?? {})) {
    const control = worker?.state?.control;
    if (control === undefined) continue;
    const id = hex(
      control.deviceId ??
        current.control?.studio?.autoTrackingWorker?.[key]?.state?.control?.deviceId
    );
    const stopsTracking =
      control.detectionMode === DetectionMode.DETECTION_MODE_SEARCHING ||
      control.detectionMode === DetectionMode.DETECTION_MODE_SHUTDOWN;
    if (!isRestoring(id) && !stopsTracking) reject(id);
  }
}
